import React, { useMemo } from 'react';
import { Alert, Badge, ButtonGroup, Card, Col, Form, Row, ToggleButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  BILLING_PERIODS,
  formatUsdAmount,
  getPlanQuote,
  getPromoBillingPeriodLabelKey,
  isPlanBillingPeriodEnabled
} from '../salesPricing';

const badgeVariantByPeriod = {
  any: 'secondary',
  monthly: 'info',
  yearly: 'dark'
};

const SalesPlanSelector = ({ plans, selectedPlanId, onSelectPlanId, billingPeriod, onBillingPeriodChange, promo }) => {
  const { t, i18n } = useTranslation();

  const selectedPlan = useMemo(
    () => plans.find(item => String(item.id) === String(selectedPlanId)) || null,
    [plans, selectedPlanId]
  );
  const selectedQuote = useMemo(
    () => getPlanQuote(selectedPlan, billingPeriod, promo),
    [billingPeriod, promo, selectedPlan]
  );

  return (
    <div>
      <Form.Group className="mb-3">
        <Form.Label>{t('SALES.BILLING_PERIOD')}</Form.Label>
        <ButtonGroup className="w-100">
          <ToggleButton
            id="billing-monthly"
            type="radio"
            variant={billingPeriod === BILLING_PERIODS.MONTHLY ? 'primary' : 'outline-primary'}
            name="billing-period"
            value={BILLING_PERIODS.MONTHLY}
            checked={billingPeriod === BILLING_PERIODS.MONTHLY}
            onChange={event => onBillingPeriodChange(event.currentTarget.value)}
          >
            {t('SALES.BILLING_PERIOD_MONTHLY')}
          </ToggleButton>
          <ToggleButton
            id="billing-yearly"
            type="radio"
            variant={billingPeriod === BILLING_PERIODS.YEARLY ? 'primary' : 'outline-primary'}
            name="billing-period"
            value={BILLING_PERIODS.YEARLY}
            checked={billingPeriod === BILLING_PERIODS.YEARLY}
            onChange={event => onBillingPeriodChange(event.currentTarget.value)}
          >
            {t('SALES.BILLING_PERIOD_YEARLY')}
          </ToggleButton>
        </ButtonGroup>
      </Form.Group>

      <div className="mb-2 fw-semi-bold">{t('SALES.PLAN_PREVIEW')}</div>
      <Row className="g-3 mb-3">
        {plans.map(plan => {
          const isSelected = String(plan.id) === String(selectedPlanId);
          const yearlyEnabled = isPlanBillingPeriodEnabled(plan, BILLING_PERIODS.YEARLY);
          const monthlyUsd = plan?.pricing?.monthly?.usd?.amount;
          const yearlyUsdPerMonth = plan?.pricing?.yearly?.usd_per_month?.amount;
          const yearlyUsdTotal = plan?.pricing?.yearly?.usd_total?.amount;
          return (
            <Col md={6} key={plan.id}>
              <Card
                role="button"
                onClick={() => onSelectPlanId(String(plan.id))}
                className={isSelected ? 'border-primary shadow-sm h-100' : 'h-100'}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <div className="fw-semi-bold">{plan.name}</div>
                      <div className="text-600 fs--1">{plan.description || t('SALES.NO_DESCRIPTION')}</div>
                    </div>
                    {promo ? (
                      <Badge bg={badgeVariantByPeriod[promo.applicable_billing_period || 'any'] || 'secondary'}>
                        {t(getPromoBillingPeriodLabelKey(promo.applicable_billing_period))}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="mb-2">
                    <div className="text-600 fs--2">{t('SALES.MONTHLY_PRICE')}</div>
                    <div className="fw-semi-bold">{formatUsdAmount(monthlyUsd, i18n.language)}</div>
                  </div>
                  <div className="mb-3">
                    <div className="text-600 fs--2">{t('SALES.YEARLY_PRICE')}</div>
                    <div className="fw-semi-bold">
                      {yearlyEnabled
                        ? `${formatUsdAmount(yearlyUsdPerMonth, i18n.language)} / ${t('SALES.PER_MONTH')} · ${formatUsdAmount(yearlyUsdTotal, i18n.language)} / ${t('SALES.PER_YEAR')}`
                        : t('SALES.NOT_AVAILABLE')}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {(plan.preview_features || []).slice(0, 6).map(feature => (
                      <Badge bg="soft-primary" text="primary" key={`${plan.id}-${feature.key}`} className="px-2 py-1">
                        {feature.label}: {String(feature.value)}
                      </Badge>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {!selectedPlan ? (
        <Alert variant="secondary" className="mb-0">{t('SALES.SELECT_PLAN_TO_PREVIEW')}</Alert>
      ) : (
        <Card className="bg-light border-0">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
              <div className="fw-semi-bold">{t('SALES.FINAL_PRICE')}</div>
              {promo ? (
                <Badge bg={badgeVariantByPeriod[promo.applicable_billing_period || 'any'] || 'secondary'}>
                  {promo.campaign_name} · {t(getPromoBillingPeriodLabelKey(promo.applicable_billing_period))}
                </Badge>
              ) : null}
            </div>

            {selectedQuote && !selectedQuote.isCompatible ? (
              <Alert variant="warning" className="mb-3">
                {t('SALES.PROMO_REQUIRES_BILLING_PERIOD', {
                  billingPeriod: t(getPromoBillingPeriodLabelKey(promo?.applicable_billing_period))
                })}
              </Alert>
            ) : null}

            <Row className="g-2">
              <Col sm={4}>
                <div className="text-600 fs--2">{t('SALES.BASE_PRICE')}</div>
                <div className="fw-semi-bold">{formatUsdAmount(selectedQuote?.baseAmount, i18n.language)}</div>
              </Col>
              <Col sm={4}>
                <div className="text-600 fs--2">{t('SALES.PROMO_DISCOUNT')}</div>
                <div className="fw-semi-bold text-success">-{formatUsdAmount(selectedQuote?.discountAmount, i18n.language)}</div>
              </Col>
              <Col sm={4}>
                <div className="text-600 fs--2">{t('SALES.FINAL_PRICE')}</div>
                <div className="fw-bold text-primary">{formatUsdAmount(selectedQuote?.finalAmount, i18n.language)}</div>
              </Col>
              <Col sm={6}>
                <div className="text-600 fs--2">{t('SALES.BILLING_PERIOD')}</div>
                <div className="fw-semi-bold">{t(getPromoBillingPeriodLabelKey(selectedQuote?.billingPeriod))}</div>
              </Col>
              <Col sm={6}>
                <div className="text-600 fs--2">{t('SALES.PER_MONTH')}</div>
                <div className="fw-semi-bold">{formatUsdAmount(selectedQuote?.basePerMonth?.amount, i18n.language)}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default SalesPlanSelector;